import { KeyboardKeyEnum } from '@/React/Shared/Type/KeyboardKeyEnum'
import type { ComponentPropsWithRef, ReactNode } from 'react'
import { Icon, type IconNameEnum } from '@/React/Shared/Component/Icon'

export interface TabsContainerProps extends ComponentPropsWithRef<'div'> {
    children?: ReactNode
}

function TabsContainer({
    children,
    className = '',
    ...restProps
}: TabsContainerProps) {
    return (
        <div
            {...restProps}
            className={className}
        >
            {children}
            <div className="border-b border-b-gray-200 -mt-px" />
        </div>
    )
}

export function TabsList({
    children,
    className = '',
    onKeyDown,
    ...restProps
}: ComponentPropsWithRef<'div'>) {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        onKeyDown?.(e)

        if (
            e.key !== KeyboardKeyEnum.ArrowRight &&
            e.key !== KeyboardKeyEnum.ArrowLeft &&
            e.key !== KeyboardKeyEnum.ArrowDown &&
            e.key !== KeyboardKeyEnum.ArrowUp &&
            e.key !== KeyboardKeyEnum.Home &&
            e.key !== KeyboardKeyEnum.End
        ) {
            return
        }

        const tabs = Array.from(
            e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]'),
        )

        if (tabs.length === 0) {
            return
        }

        const activeEl = document.activeElement as HTMLElement | null
        const currentIndex = tabs.findIndex((t) => t === activeEl)

        let nextIndex = 0

        if (
            e.key === KeyboardKeyEnum.ArrowRight ||
            e.key === KeyboardKeyEnum.ArrowDown
        ) {
            nextIndex =
                currentIndex === -1 ? 0 : (currentIndex + 1) % tabs.length
        } else if (
            e.key === KeyboardKeyEnum.ArrowLeft ||
            e.key === KeyboardKeyEnum.ArrowUp
        ) {
            nextIndex =
                currentIndex === -1
                    ? tabs.length - 1
                    : (currentIndex - 1 + tabs.length) % tabs.length
        } else if (e.key === KeyboardKeyEnum.Home) {
            nextIndex = 0
        } else if (e.key === KeyboardKeyEnum.End) {
            nextIndex = tabs.length - 1
        }

        e.preventDefault()
        tabs[nextIndex].focus()

        if (!tabs[nextIndex].getAttribute('href')) {
            tabs[nextIndex].click()
        }
    }

    return (
        <div
            role="tablist"
            aria-orientation="horizontal"
            onKeyDown={handleKeyDown}
            {...restProps}
            className={`flex items-center gap-4 lg:gap-8 ${className}`.trim()}
        >
            {children}
        </div>
    )
}

export interface TabsItemProps extends ComponentPropsWithRef<'a'> {
    isSelected?: boolean
    iconName?: IconNameEnum | string | null
    iconClass?: string
    children: ReactNode
}

export function TabsItem({
    isSelected = false,
    iconName,
    iconClass = 'w-4 h-4',
    children,
    className = '',
    onClick,
    onKeyDown,
    ...restProps
}: TabsItemProps) {
    const activeClasses = isSelected
        ? 'border-b-primary text-primary'
        : 'border-transparent text-gray-700 hover:text-gray-900'

    const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        onKeyDown?.(e)

        if (
            e.key === KeyboardKeyEnum.Space ||
            e.key === KeyboardKeyEnum.Enter
        ) {
            if (!restProps.href && onClick) {
                e.preventDefault()
                onClick(e as any)
            }
        }
    }

    return (
        <a
            role="tab"
            aria-selected={isSelected}
            tabIndex={isSelected ? 0 : -1}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            {...restProps}
            className={`cursor-pointer py-3 font-medium border-b-2 flex items-center gap-1 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none rounded-t-lg ${activeClasses} ${className}`.trim()}
        >
            {iconName ? (
                <Icon
                    name={iconName as IconNameEnum}
                    className={iconClass}
                    aria-hidden="true"
                />
            ) : null}
            <span>{children}</span>
        </a>
    )
}

export const Tabs = Object.assign(TabsContainer, {
    Container: TabsContainer,
    List: TabsList,
    Item: TabsItem,
})
